import { useCallback, useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import api from "services/api";

import getQueryParams from "common/get-query-params";
import encodeQueryParams from "common/encode-query-params";
import serveRequestErrors from "common/serve-request-errors";
import serveRequestSuccess from "common/serve-request-success";

import CONSTANTS from "constants/constant";

import ActionEnum from "enum/action";

import ErrorResponseDto from "dto/services/error-response-dto";
import PageSolutionsDto, { PageSolutionsTableCreatePayloadDto, PageSolutionsTableQueryParamsDto, PageSolutionsTableUpdatePayloadDto, SolutionCardDto } from "dto/pages/solutions/page-solutions-dto";

import Button from "components/app-button";
import AppHeading from "components/app-heading";
import AppCategoryTab from "components/app-category-tab";
import SolutionTable from "components/pages/page-solutions/app-solutions-table";

const PageSolutions = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const queryParams = useRef(getQueryParams<PageSolutionsTableQueryParamsDto>(searchParams, { page: 0, size: 100 }));
	const [header, setHeader] = useState("");
	const [isFetching, setIsFetching] = useState(false);
	const [disableAddNew, setDisableAddNew] = useState(false);
	const [servicesCard, setServicesCard] = useState<PageSolutionsDto>();
	const [contentTitle, setContentTitle] = useState<SolutionCardDto[]>([]);
	const [newRow, setNewRow] = useState<PageSolutionsTableCreatePayloadDto>();
	const [categoriesTitleData, setCategoriesTitleData] = useState<SolutionCardDto[]>([]);
	const [selectedContentId, setSelectedContentId] = useState<string>("");
	const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");

	const onHandleGetServices = useCallback(async () => {
		let response = null;

		setIsFetching(true);

		try {
			const { page } = queryParams.current;

			const payload = { size: 10, page };

			const isAllCategory = selectedCategoryId === "all";

			const selectedId = isAllCategory ? selectedContentId : selectedCategoryId;

			if (!selectedId) return;

			response = isAllCategory ? await api.get.solution.allServices(selectedId, payload) : await api.get.solution.servicesCard(selectedId, payload);

			setServicesCard(response.data?.data?.list);
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		} finally {
			setIsFetching(false);
		}
	}, [selectedCategoryId, selectedContentId]);

	const onHandleGetCategoriesTitle = useCallback(async () => {
		try {
			const { page } = queryParams.current;

			const payload = { size: 10, page };

			const response = await api.get.solution.services(selectedContentId, payload);

			const allCategory = { title: "all", id: "all", status: "active" };

			const dataContent: SolutionCardDto[] = response.data?.data?.list?.content || [];

			const activeDataContent = dataContent.filter((item) => item.status === "active");

			const filterDataContent: SolutionCardDto[] = [allCategory, ...activeDataContent];

			const mapDataContent = filterDataContent.map((m, i) => ({ ...m, indexKey: i }));

			setCategoriesTitleData(mapDataContent);

			setSelectedCategoryId(mapDataContent[0].id);
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
	}, [selectedContentId]);

	const onHandleGetContentTitle = useCallback(async () => {
		try {
			const response = await api.get.solution.solutionTitle();

			const dataContent: SolutionCardDto[] = response.data?.data?.list;

			const activeDataContent = dataContent.filter((item) => item.status === "active");

			const mapDataContent = activeDataContent.map((m, i) => ({ ...m, indexKey: i }));

			setContentTitle(mapDataContent);

			setHeader(mapDataContent[0].title);

			setSelectedContentId(mapDataContent[0].id);
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
	}, []);

	//prettier-ignore
	const onHandleAddSolutions = useCallback(async (payload: PageSolutionsTableCreatePayloadDto) => {
		let response = null;

		try {
			await api.post.solution.create(payload);

			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}

		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.CREATED, ActionEnum.Create);

			if (servicesCard && servicesCard?.numberOfElements === servicesCard.size) {
				 queryParams.current.page = servicesCard.totalElements % servicesCard.size === 0 ? servicesCard.totalPages : servicesCard.totalPages - 1;
			}

			onHandleGetServices();
		}
	}, [onHandleGetServices, servicesCard]);

	//prettier-ignore
	const onUpdateSolutions = useCallback(async (payload: PageSolutionsTableUpdatePayloadDto, page?: number) => {
		let response = null;

		try {
			await api.put.solution.update({ id: payload.id }, payload);

			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
		
		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);

			if (typeof page === "number") {
				 queryParams.current.page = page;
			}
	
			onHandleGetServices();
		}
	}, [onHandleGetServices]);

	//prettier-ignore
	const onHandlePageChange = useCallback((page: number) => {
		queryParams.current.page = page;

		memoSetSearchParams.current(encodeQueryParams(queryParams.current));

		onHandleGetServices();
	}, [onHandleGetServices]);

	const onHandleAddNew = () => {
		setDisableAddNew(true);
		setNewRow({ logo: "", title: "", category: selectedCategoryId === "all" ? "" : selectedCategoryId, status: "active" });
	};

	//prettier-ignore
	const onHandleSelect = useCallback((tab: any) => {
		const categoryId = tab.id;

		setDisableAddNew(false);

		queryParams.current.page = 0;

		setSelectedCategoryId(categoryId);

		if (newRow) {setNewRow((prev: any) => ({ ...prev, category: categoryId === "all" ? "" : categoryId })); }
	}, [newRow]);

	const onHandleSelectContent = useCallback((tab: any) => {
		const categoryId = tab.id;

		setDisableAddNew(false);

		setHeader(tab.title);

		queryParams.current.page = 0;

		setSelectedContentId(categoryId);

		setNewRow(undefined);
	}, []);

	useEffect(() => {
		onHandleGetContentTitle();
	}, [onHandleGetContentTitle]);

	useEffect(() => {
		if (selectedContentId) onHandleGetCategoriesTitle();
	}, [onHandleGetCategoriesTitle, selectedContentId]);

	useEffect(() => {
		if (selectedCategoryId) onHandleGetServices();
	}, [onHandleGetServices, selectedCategoryId]);

	return (
		<div className="page-solutions">
			<div className="solutions">
				<AppCategoryTab tabs={contentTitle} onSelect={onHandleSelectContent} customBorder />

				<AppHeading title={header}></AppHeading>

				<AppCategoryTab tabs={categoriesTitleData || []} onSelect={onHandleSelect} />

				<div className="solutions__button">
					<Button label="Add New" disabled={disableAddNew} onClick={onHandleAddNew} />
				</div>

				{/* prettier-ignore */}
				<SolutionTable data={servicesCard?.content || []} setDisableAddNew={setDisableAddNew} newRow={newRow} setNewRow={setNewRow} onUpdateData={onUpdateSolutions} onCreateData={onHandleAddSolutions} onHandlePageChange={onHandlePageChange} fetching={isFetching} paginationTable={{ currentPage: servicesCard?.number || 0, totalPages: servicesCard?.totalPages || 0, totalElements: servicesCard?.totalElements || 0, numberOfElements: servicesCard?.numberOfElements || 0, tableSize: servicesCard?.size || 10 }} id={selectedContentId} selectedCategoryId={selectedCategoryId} />
			</div>
		</div>
	);
};

export default PageSolutions;
